import React, { useMemo, useRef, useState } from "react";
import {
  Bookmark,
  BookmarkPlus,
  Check,
  ChevronDown,
  CircleHelp,
  MoreHorizontal,
  Pencil,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ConfirmModal } from "@/components/Common/ConfirmModal/ConfirmModal";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { SavedConfigContentsDialog } from "./SavedConfigContentsDialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useSavedDeploymentConfigStore } from "@/store/savedDeploymentConfig";
import {
  SavedDeploymentConfig,
  SavedDeploymentConfigKind,
  SavedDeploymentConfigPayload
} from "@/types/savedDeploymentConfig";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";
import { cn } from "@/lib/utils";
import { SaveConfigDialog } from "./SaveConfigButton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import env from "@/env";

type SavedConfigsSelectProps = {
  kind: SavedDeploymentConfigKind;
  selectedId?: string | null;
  onSelect: (config: SavedDeploymentConfig) => void | Promise<void>;
  /** Current form snapshot for Save / Update */
  savePayload?: SavedDeploymentConfigPayload;
  canSave?: boolean;
  /** When a config is loaded, false if the form matches that config. Defaults to true. */
  canUpdateLoaded?: boolean;
  selectedConfig?: SavedDeploymentConfig | null;
  onSaved?: (config: SavedDeploymentConfig) => void;
  className?: string;
};

export const SavedConfigsSelect: React.FC<SavedConfigsSelectProps> = ({
  kind,
  selectedId,
  onSelect,
  savePayload,
  canSave = false,
  canUpdateLoaded = true,
  selectedConfig,
  onSaved,
  className
}) => {
  const { toast } = useToast();
  const storage = useSavedDeploymentConfigStore((s) => s.storage);
  const list = useSavedDeploymentConfigStore((s) => s.list);
  const update = useSavedDeploymentConfigStore((s) => s.update);
  const remove = useSavedDeploymentConfigStore((s) => s.remove);
  const findByName = useSavedDeploymentConfigStore((s) => s.findByName);

  const create = useSavedDeploymentConfigStore((s) => s.create);

  const configs = useMemo(() => list(kind), [list, kind, storage]);

  const [renameTarget, setRenameTarget] = useState<SavedDeploymentConfig | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<SavedDeploymentConfig | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [contentsOpen, setContentsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loadTooltipOpen, setLoadTooltipOpen] = useState(false);
  const suppressLoadTooltipRef = useRef(false);
  const suppressLoadTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const selected = selectedConfig ?? configs.find((c) => c.id === selectedId);
  const showSaveActions = Boolean(savePayload);
  const canClickSave = canSave && (!selected || canUpdateLoaded);

  const saveDisabledReason = useMemo(() => {
    if (canClickSave) return null;
    if (canSave && selected && !canUpdateLoaded) {
      return "No changes to save — the form matches the loaded configuration.";
    }

    const hasHardware = Boolean(savePayload?.hardware || savePayload?.hardwareId);
    const hasSshSelection =
      (savePayload?.selectedSshKeyIds?.length ?? 0) > 0 ||
      (Array.isArray(savePayload?.sshKeys) &&
        savePayload.sshKeys.some(
          (sshKey) => Boolean(sshKey.key?.trim()) && Boolean(sshKey.value?.trim())
        ));

    if (kind === "vm") {
      if (!hasHardware) {
        return "Select a GPU/processor first. It’s saved with the config (if still available when you load it later).";
      }
      if (!hasSshSelection) {
        return "Add at least one SSH key to save. Your selected GPU is included in the saved config.";
      }
      return "Finish required VM fields before saving. Your selected GPU is included in the saved config.";
    }

    if (!hasHardware) {
      return "Select a GPU/processor first. It’s saved with the config (if still available when you load it later).";
    }
    return "Finish required configure fields (image, port, etc.) before saving. Your selected GPU is included in the saved config.";
  }, [canClickSave, canSave, canUpdateLoaded, selected, kind, savePayload]);

  const handleSelect = async (config: SavedDeploymentConfig) => {
    setIsApplying(true);
    setMenuOpen(false);
    try {
      await onSelect(config);
    } finally {
      setIsApplying(false);
    }
  };

  /** Overwrite the loaded config with the current form (settings, not just the name). */
  const handleUpdateInPlace = () => {
    if (!savePayload || !selected) return;
    const config = create({
      name: selected.name,
      kind,
      payload: savePayload,
      overwriteId: selected.id
    });
    trackEvent(AnalyticsEventType.SAVED_DEPLOYMENT_CONFIG_UPDATED, {
      kind,
      schemaVersion: config.schemaVersion,
      action: "overwrite"
    });
    toast({
      title: "Configuration updated",
      description: `Saved current settings to “${config.name}”.`
    });
    onSaved?.(config);
  };

  const openSaveNew = () => {
    setMenuOpen(false);
    setSaveOpen(true);
  };

  const handleSaveClick = () => {
    if (selected) {
      handleUpdateInPlace();
      return;
    }
    openSaveNew();
  };

  const handleRename = () => {
    if (!renameTarget) return;
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    const clash = findByName(kind, trimmed, renameTarget.id);
    if (clash) {
      toast({
        title: "Name already in use",
        description: `Another ${kind} configuration is already named “${trimmed}”.`,
        variant: "error"
      });
      return;
    }
    update(renameTarget.id, { name: trimmed });
    trackEvent(AnalyticsEventType.SAVED_DEPLOYMENT_CONFIG_UPDATED, {
      kind,
      schemaVersion: renameTarget.schemaVersion,
      action: "rename"
    });
    toast({ title: "Configuration renamed", description: trimmed });
    setRenameTarget(null);
    clearStuckPointerEvents();
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    remove(deleteTarget.id);
    trackEvent(AnalyticsEventType.SAVED_DEPLOYMENT_CONFIG_DELETED, {
      kind,
      schemaVersion: deleteTarget.schemaVersion
    });
    toast({ title: "Configuration deleted", description: deleteTarget.name });
    setDeleteTarget(null);
    clearStuckPointerEvents();
  };

  /** Close dropdowns before opening a dialog to avoid Radix leaving body pointer-events: none. */
  const clearStuckPointerEvents = () => {
    requestAnimationFrame(() => {
      if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    });
  };

  const openRenameDialog = (config: SavedDeploymentConfig) => {
    setMenuOpen(false);
    window.setTimeout(() => {
      setRenameTarget(config);
      setRenameValue(config.name);
      clearStuckPointerEvents();
    }, 0);
  };

  const openDeleteDialog = (config: SavedDeploymentConfig) => {
    setMenuOpen(false);
    window.setTimeout(() => {
      setDeleteTarget(config);
      clearStuckPointerEvents();
    }, 0);
  };

  const handleMenuOpenChange = (open: boolean) => {
    setMenuOpen(open);
    if (!open) {
      setLoadTooltipOpen(false);
      suppressLoadTooltipRef.current = true;
      if (suppressLoadTooltipTimeoutRef.current) {
        clearTimeout(suppressLoadTooltipTimeoutRef.current);
      }
      suppressLoadTooltipTimeoutRef.current = setTimeout(() => {
        suppressLoadTooltipRef.current = false;
      }, 500);
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  };

  const handleLoadTooltipOpenChange = (open: boolean) => {
    if (open && (menuOpen || suppressLoadTooltipRef.current)) {
      return;
    }
    setLoadTooltipOpen(open);
  };

  React.useEffect(
    () => () => {
      if (suppressLoadTooltipTimeoutRef.current) {
        clearTimeout(suppressLoadTooltipTimeoutRef.current);
      }
    },
    []
  );

  if (!env.FEATUREFLAG_SAVED_DEPLOYMENT_CONFIGS) {
    return null;
  }

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <div className={cn("flex items-center gap-2", className)}>
          {showSaveActions && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    disabled={!canClickSave || isApplying}
                    onClick={handleSaveClick}
                  >
                    <BookmarkPlus className="h-3.5 w-3.5 shrink-0" />
                    <span className="whitespace-nowrap">Save config</span>
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[280px]">
                {canClickSave
                  ? selected
                    ? `Overwrite “${selected.name}” with the current form settings (including selected processor/GPU). Use Load config → Save as new for a copy.`
                    : kind === "vm"
                      ? "Save the current form (including selected processor/GPU, SSH keys, and other bookmarked fields) as a named configuration. No need to deploy."
                      : "Save the current form (including selected processor/GPU and other bookmarked fields) as a named configuration. No need to deploy."
                  : saveDisabledReason}
              </TooltipContent>
            </Tooltip>
          )}

          <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
            <Tooltip
              open={menuOpen ? false : loadTooltipOpen}
              onOpenChange={handleLoadTooltipOpenChange}
            >
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="max-w-[220px] gap-2"
                      disabled={isApplying}
                    >
                      <Bookmark className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">
                        {selected ? selected.name : "Load config"}
                      </span>
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[260px]">
                Click to load a previously saved configuration, including the selected
                processor/GPU when still available. Bookmark icons mark other included fields.
              </TooltipContent>
            </Tooltip>
          <DropdownMenuContent
            align="end"
            className="w-[300px]"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex items-center justify-between gap-2 px-2 py-1.5">
              <span className="label-small text-foreground">
                {kind === "vm" ? "Virtual Machine" : "Container"} saved configurations
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                    aria-label="What's included"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMenuOpen(false);
                      setContentsOpen(true);
                    }}
                  >
                    <CircleHelp className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">What&apos;s included</TooltipContent>
              </Tooltip>
            </div>
            <DropdownMenuSeparator />
            {configs.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">
                No saved configurations yet. Use <span className="font-medium">Save config</span>{" "}
                once your form is ready.
              </div>
            ) : (
              configs.map((config) => {
                const isSelected = selectedId === config.id;
                return (
                <div
                  key={config.id}
                  className={cn(
                    "flex items-center gap-1 rounded-md pr-1 transition-colors",
                    isSelected ? "bg-accent" : "hover:bg-muted"
                  )}
                >
                  <button
                    type="button"
                    className={cn(
                      "flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-sm",
                      isSelected && "font-medium text-foreground"
                    )}
                    onClick={() => void handleSelect(config)}
                  >
                    {isSelected ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                    ) : (
                      <Bookmark className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="truncate">{config.name}</span>
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[160px]">
                      <DropdownMenuItem
                        onSelect={(event) => {
                          event.preventDefault();
                          openRenameDialog(config);
                        }}
                      >
                        <Pencil className="mr-2 h-3.5 w-3.5" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-dark-300 focus:text-red-dark-300"
                        onSelect={(event) => {
                          event.preventDefault();
                          openDeleteDialog(config);
                        }}
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                );
              })
            )}

            {showSaveActions && selected && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  disabled={!canSave}
                  onSelect={(e) => {
                    e.preventDefault();
                    if (!canSave) return;
                    openSaveNew();
                  }}
                  className="gap-2"
                >
                  <BookmarkPlus className="h-3.5 w-3.5" />
                  Save as new…
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </TooltipProvider>

      {savePayload && (
        <SaveConfigDialog
          kind={kind}
          payload={savePayload}
          selectedConfig={selected}
          isOpen={saveOpen}
          mode="new"
          onOpenChange={setSaveOpen}
          onSaved={onSaved}
        />
      )}

      <SavedConfigContentsDialog
        kind={kind}
        isOpen={contentsOpen}
        onOpenChange={setContentsOpen}
      />

      <Dialog
        open={Boolean(renameTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setRenameTarget(null);
            clearStuckPointerEvents();
          }
        }}
      >
        <DialogContent className="w-full gap-0 px-0 py-0" showCloseIcon={false}>
          <DialogHeader className="px-8 pt-4">
            <DialogTitle className="title-h4 text-foreground">Rename configuration</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 px-8 py-4">
            <label className="label-small text-foreground" htmlFor="saved-config-rename">
              Name <span className="text-blue-light-300">*</span>
            </label>
            <Input
              id="saved-config-rename"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="My H100 vLLM"
              maxLength={255}
            />
          </div>
          <DialogFooter className="flex flex-row !justify-center gap-2 rounded-b-lg border-t px-4 py-4">
            <Button type="button" variant="outline" size="sm" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!renameValue.trim()}
              onClick={handleRename}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        title="Delete configuration?"
        description={
          deleteTarget
            ? `“${deleteTarget.name}” will be removed from this browser. This cannot be undone.`
            : null
        }
        submitLabel="Delete"
        submitButtonVariant="error"
        onClose={() => {
          setDeleteTarget(null);
          clearStuckPointerEvents();
        }}
        onSubmit={handleDelete}
      />
    </>
  );
};
